import type { PhieuXuatDieuChuyenChiTiet, PhieuXuatDieuChuyenFormData } from '@/types/schemas';
import { transformDocumentNumber } from '@/components/custom/erp';
import { isValidUUID } from '@/lib/uuid-validator';
import { roundNumber } from '@/lib/formatUtils';
import { FormFieldState } from '../hooks';

/**
 * Transform detail rows for API submission
 * @param detailRows - Array of detail row data from the form
 * @returns Transformed detail rows ready for API submission
 */
export const transformDetailRows = (
  detailRows: PhieuXuatDieuChuyenChiTiet[],
  formMode: string,
  ma_kho?: string | null
) => {
  return detailRows.map((row, index: number) => ({
    line: index + 1,
    uuid: row.uuid && isValidUUID(row.uuid) && formMode !== 'add' ? row.uuid : undefined,
    ma_vt: row.ma_vt_data?.uuid || row.ma_vt || null,
    dvt: row.dvt || row.ma_vt_data?.dvt || null,
    ma_kho: ma_kho || null,
    ma_khon: row.ma_khon_data?.uuid || row.ma_khon || null,
    ma_vi_tri: row.ma_vi_tri_data?.uuid || row.ma_vi_tri || null,
    ma_vi_trin: row.ma_vi_trin_data?.uuid || row.ma_vi_trin || null,
    sl_ton: row.sl_ton || 0,
    so_luong: roundNumber(row.so_luong, 6),
    px_dd: row.px_dd || 0,
    gia_nt: roundNumber(row.gia_nt, 6),
    tien_nt: roundNumber(row.tien_nt || (Number(row.gia_nt) || 0) * (Number(row.so_luong) || 0), 6),
    gia: roundNumber(row.gia, 6),
    tien: roundNumber(row.tien, 6),
    tk_vt: row.tk_vt_data?.uuid || row.tk_vt || null,
    ma_nx: row.ma_nx_data?.uuid || row.ma_nx || null,
    tk_du: row.tk_du_data?.uuid || row.tk_du || null,
    ghi_chu: row.ghi_chu || null,
    ma_bp: row.ma_bp_data?.uuid || row.ma_bp || null,
    ma_vv: row.ma_vv_data?.uuid || row.ma_vv || null,
    ma_hd: row.ma_hd_data?.uuid || row.ma_hd || null,
    ma_dtt: row.ma_dtt_data?.uuid || row.ma_dtt || null,
    ma_ku: row.ma_ku_data?.uuid || row.ma_ku || null,
    ma_phi: row.ma_phi_data?.uuid || row.ma_phi || null,
    ma_sp: row.ma_sp_data?.uuid || row.ma_sp || null,
    ma_lsx: row.ma_lsx_data?.uuid || row.ma_lsx || null,
    ma_cp0: row.ma_cp0_data?.uuid || row.ma_cp0 || null,
    so_ct_pn: row.so_ct_pn || null
  }));
};

/**
 * Transform all form data for submission
 * @param data - Form data from the form submission
 * @param state - Form field state containing references to selected entities
 * @param detailRows - Array of detail row data
 * @param entityUnit - Entity unit information
 * @returns Transformed data ready for API submission
 */
export const transformFormData = (
  data: PhieuXuatDieuChuyenFormData,
  state: FormFieldState,
  detailRows: PhieuXuatDieuChuyenChiTiet[] = [],
  entityUnit: { uuid?: string } | null | undefined,
  so_luong: number,
  thanh_tien: number,
  formMode: string
) => {
  const chi_tiet = transformDetailRows(detailRows, formMode, state.selectedWarehouse?.uuid);

  return {
    ...data,
    unit_id: entityUnit?.uuid || null,
    ma_ngv: '3',
    ma_kho: state.selectedWarehouse?.uuid || null,
    ma_kh_vc: state.selectedCustomer?.uuid || null,
    ten_kh_vc: data.ten_kh_vc || state.selectedCustomer?.customer_name || null,
    ma_ptvc: state.selectedVehicle?.uuid || null,
    ngay_ct: data.ngay_ct || null,
    ngay_lct: data.ngay_ct || null,
    ma_nt: data.ma_nt,
    ty_gia: data.ty_gia || 1,
    status: data.status || '0',
    ma_tthddt: data.ma_tthddt || '0',
    so_ct_hddt: data.so_ct_hddt || null,
    ma_pttt: data.ma_pttt || null,
    ngay_ct_hddt: data.ngay_ct_hddt || null,
    lenh_dd: data.lenh_dd || null,
    so_ct2_hddt: data.so_ct2_hddt || null,
    ngay_dd: data.ngay_dd || null,
    ma_mau_ct_hddt: data.ma_mau_ct_hddt || null,
    nguoi_dd: data.nguoi_dd || null,
    viec_dd: data.viec_dd || null,
    so_hd_vc: data.so_hd_vc || null,
    ly_do_huy: data.ly_do_huy || null,
    ly_do: data.ly_do || null,
    ...transformDocumentNumber(state.selectedDocument, state.selectedDocumentNumber),
    t_so_luong: roundNumber(so_luong, 6),
    t_tien_nt: roundNumber(thanh_tien, 6),
    t_tien: roundNumber(thanh_tien, 6),
    dien_giai: data.dien_giai || null,

    chi_tiet
  };
};
